import {storeToRefs} from 'pinia';
import { useOwnerStore } from '@/store/owner';
import {useTrademarkStore} from '@/store/mark';
import {useProductStore} from '@/store/good';
import useDomain from '@/composables/domainDependable';
import {useCartReferenceStore} from '@/store/cart-reference';
import {computed} from 'vue';
import {
    type ApplicationInterface,
    type MarkImageInterface,
    MarkOwnerType,
    type MarkWordInterface
} from '@/core/interface/api-interface';
import {getFirstAndLastNameFromString, isBusinessNumber} from '@/core/util/owner-utils';
import {useApplicationStore} from '@/store/application';
import { useContactStore } from '@/store/contact';

export const useApplicationData = () => {
    const {email: contactEmail, names: contactName} = storeToRefs(useContactStore());
    const {trademarkWord, trademarkImage, uploadedAsWordImage} = storeToRefs(useTrademarkStore());
    const {goods} = storeToRefs(useProductStore());
    const {ownerDetails, ownerNames, ownerType, businessNumber, country} = storeToRefs(useOwnerStore());
    const {markSource} = useDomain();
    const {appReference} = storeToRefs(useApplicationStore());
    const {cartReference} = storeToRefs(useCartReferenceStore());

    const markOwners = computed(() => {
        const validOwnerName = ownerNames.value.filter(ownerName => ownerName.trim() !== '');

        return validOwnerName.map(ownerName => {
            let markOwnerType = ownerType.value;
            if (markOwnerType === null) {
                markOwnerType = MarkOwnerType.PERSON;
            }

            let name = {
                firstName: '',
                lastName: ''
            };
            let companyNb = null;
            let businessNb = null;

            if (markOwnerType == MarkOwnerType.BUSINESS) {
                if (isBusinessNumber(businessNumber.value, country.value)) {
                    businessNb = businessNumber.value;
                } else {
                    companyNb = businessNumber.value;
                }
            } else {
                name = getFirstAndLastNameFromString(ownerName);
            }


            return {
                markOwnerType: markOwnerType,
                firstName: name.firstName !== '' ? name.firstName : null,
                lastName: name.lastName !== '' ? name.lastName : null,
                businessName: markOwnerType == MarkOwnerType.BUSINESS ? ownerName : null,
                citizenCountryCode: country.value !== '' ? country.value : null,
                businessNumber: businessNb,
                companyNumber: companyNb
            };
        });
    });

    const markContact = computed(() => {
        let names: {firstName: null|string; lastName: null|string;} = {
            firstName: null,
            lastName: null
        };

        if (contactName.value.length > 0) {
            names = getFirstAndLastNameFromString(contactName.value);
        }

        return {
            firstName: names.firstName,
            lastName: names.lastName,
            email: contactEmail.value,
            phone: ownerDetails.value.phone,
        };
    });

    const markWord = computed(() => {
        let markWord: MarkWordInterface|null = null;

        if (trademarkWord.value) {
            markWord = { word: trademarkWord.value };
        }

        return markWord;
    });

    const markImage = computed(() => {
        let markImage: MarkImageInterface | null = null;

        if (trademarkImage.value) {
            markImage = {
                imageRef: trademarkImage.value.ref,
                uploadedAsWordImage: uploadedAsWordImage.value
            }
        }

        return markImage;
    });

    const applicationData = computed<ApplicationInterface>(() => {
        return {
            applicationRef: appReference.value,
            cartRef: cartReference.value,
            mark: {
                markSource: markSource,
                markWord: markWord.value,
                markImage: markImage.value,
                markContact: markContact.value,
                markOwners: markOwners.value,
                markOwnerDetail: {
                    city: ownerDetails.value.city,
                    address: ownerDetails.value.address,
                    suburb: ownerDetails.value.suburb,
                    postCode: ownerDetails.value.postCode,
                    region: ownerDetails.value.stateRegion,
                    countryCode: ownerDetails.value.country,
                    email: ownerDetails.value.email,
                    phone: ownerDetails.value.phone,
                    declaration: true,
                },
                markGoods: goods.value
            }
        }
    });

    return {applicationData};
}
